import React, { useEffect, useMemo, useState } from "react";
import api from "../../api";
import { useAuth } from "../../AuthContext";
import { useNavigate } from "react-router-dom";

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [stats, setStats] = useState({
    posts: 0,
    gallery: 0,
    comments: 0,
    categories: 0,
    users: 0,
    newsletter_active: 0,
    newsletter_inactive: 0,
    posts_without_categories: 0,
    posts_without_image: 0,
  });

  const [latestPosts, setLatestPosts] = useState([]);
  const [latestUsers, setLatestUsers] = useState([]);
  const [latestComments, setLatestComments] = useState([]);
  const [latestNewsletterSubscribers, setLatestNewsletterSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);

        const response = await api.get("/admin/stats");

        setStats({
          posts: response.data.posts || 0,
          gallery: response.data.gallery || 0,
          comments: response.data.comments || 0,
          categories: response.data.categories || 0,
          users: response.data.users || 0,
          newsletter_active: response.data.newsletter_active || 0,
          newsletter_inactive: response.data.newsletter_inactive || 0,
          posts_without_categories: response.data.posts_without_categories || 0,
          posts_without_image: response.data.posts_without_image || 0,
        });

        setLatestPosts(response.data.latest_posts || []);
        setLatestUsers(response.data.latest_users || []);
        setLatestComments(response.data.latest_comments || []);
        setLatestNewsletterSubscribers(
          response.data.latest_newsletter_subscribers || []
        );
      } catch (err) {
        console.error("Erreur dashboard :", err);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const quickStats = useMemo(
    () => [
      {
        label: "Utilisateurs",
        value: stats.users,
        accent: "text-cyan-400",
        helper: "Comptes inscrits sur la plateforme",
      },
      {
        label: "Abonnés actifs",
        value: stats.newsletter_active,
        accent: "text-green-400",
        helper: "Destinataires actifs de la newsletter",
      },
      {
        label: "Désabonnés",
        value: stats.newsletter_inactive,
        accent: "text-red-400",
        helper: "Abonnements désactivés",
      },
      {
        label: "Posts",
        value: stats.posts,
        accent: "text-blue-400",
        helper: "Articles publiés ou enregistrés",
      },
      {
        label: "Commentaires",
        value: stats.comments,
        accent: "text-amber-400",
        helper: "Interactions laissées sur les posts",
      },
      {
        label: "Images",
        value: stats.gallery,
        accent: "text-violet-400",
        helper: "Éléments présents dans la galerie",
      },
      {
        label: "Catégories",
        value: stats.categories,
        accent: "text-emerald-400",
        helper: "Classes disponibles pour organiser le contenu",
      },
      {
        label: "Posts sans catégorie",
        value: stats.posts_without_categories,
        accent: "text-orange-400",
        helper: "Articles à mieux organiser",
      },
      {
        label: "Posts sans photo",
        value: stats.posts_without_image,
        accent: "text-yellow-400",
        helper: "Articles sans image principale",
      },
    ],
    [stats]
  );

  const quickActions = [
    {
      title: "Créer un post",
      description: "Ajoutez rapidement un nouvel article au site.",
      emoji: "➕",
      className: "hover:border-green-500/40 hover:bg-green-500/5",
      iconClassName: "bg-green-500/15 text-green-300",
      onClick: () => navigate("/dashboard/posts/create"),
    },
    {
      title: "Créer une catégorie",
      description: "Ajoutez une nouvelle catégorie pour organiser vos posts.",
      emoji: "🏷️",
      className: "hover:border-emerald-500/40 hover:bg-emerald-500/5",
      iconClassName: "bg-emerald-500/15 text-emerald-300",
      onClick: () => navigate("/dashboard/categories/create"),
    },
    {
      title: "Gérer la galerie",
      description: "Consultez et organisez les visuels du site.",
      emoji: "📸",
      className: "hover:border-violet-500/40 hover:bg-violet-500/5",
      iconClassName: "bg-violet-500/15 text-violet-300",
      onClick: () => navigate("/dashboard/galerie"),
    },
    {
      title: "Voir les posts",
      description: "Accédez à la liste complète des articles du dashboard.",
      emoji: "📰",
      className: "hover:border-blue-500/40 hover:bg-blue-500/5",
      iconClassName: "bg-blue-500/15 text-blue-300",
      onClick: () => navigate("/dashboard/posts"),
    },
    {
      title: "Mail libre",
      description: "Envoyez une newsletter libre à vos abonnés actifs.",
      emoji: "✉️",
      className: "hover:border-green-500/40 hover:bg-green-500/5",
      iconClassName: "bg-green-500/15 text-green-300",
      onClick: () => navigate("/dashboard/newsletter/create"),
    },
    {
      title: "Partager un post",
      description: "Créez un envoi newsletter à partir d'un article existant.",
      emoji: "📨",
      className: "hover:border-amber-500/40 hover:bg-amber-500/5",
      iconClassName: "bg-amber-500/15 text-amber-300",
      onClick: () => navigate("/dashboard/newsletter/share-post"),
    },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100">
        <main className="container mx-auto p-6">
          <div className="rounded-2xl border border-slate-800 bg-slate-900 p-6 shadow-sm">
            <p className="text-slate-300">Chargement du dashboard...</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <main className="container mx-auto p-6">
        <section className="mb-8 overflow-hidden rounded-3xl bg-gradient-to-r from-slate-900 via-slate-800 to-blue-900 text-white shadow-sm">
          <div className="p-8 md:p-10">
            <p className="mb-3 inline-flex rounded-full bg-white/10 px-3 py-1 text-sm font-medium text-blue-100">
              Dashboard principal
            </p>
            <h1 className="mb-3 text-3xl font-bold md:text-4xl">
              Bienvenue {user?.firstname || "Admin"} 👋
            </h1>
            <p className="max-w-3xl text-sm leading-7 text-slate-200 md:text-base">
              Retrouvez ici en priorité les dernières inscriptions du site et les
              nouveaux abonnés newsletter, puis un aperçu global de votre espace
              d'administration.
            </p>
          </div>
        </section>

        <section className="mb-8 grid grid-cols-1 gap-6 xl:grid-cols-2">
          <div className="rounded-2xl border border-cyan-500/20 bg-slate-900 p-6 shadow-sm">
            <div className="mb-5 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-white">
                  Dernières inscriptions au site
                </h2>
                <p className="mt-1 text-sm text-slate-400">
                  Les utilisateurs les plus récents.
                </p>
              </div>
              <button
                onClick={() => navigate("/dashboard/users")}
                className="rounded-xl border border-slate-700 px-4 py-2 text-sm font-medium text-slate-200 transition hover:bg-slate-800"
              >
                Voir tous les utilisateurs
              </button>
            </div>

            {latestUsers.length > 0 ? (
              <div className="space-y-3">
                {latestUsers.map((latestUser) => (
                  <div
                    key={latestUser.id}
                    className="rounded-xl border border-slate-800 bg-slate-950 p-4"
                  >
                    <div className="flex items-center gap-4">
                      <div className="flex shrink-0 justify-center">
                        {latestUser.avatar ? (
                          <img
                            src={`${process.env.REACT_APP_IMG_URL}/${latestUser.avatar}`}
                            alt={`${latestUser.firstname} ${latestUser.lastname}`}
                            className="h-12 w-12 rounded-full border border-slate-700 object-cover"
                          />
                        ) : (
                          <div className="flex h-12 w-12 items-center justify-center rounded-full border border-slate-700 bg-slate-800 text-xs text-slate-400">
                            —
                          </div>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-semibold text-white">
                          {latestUser.firstname} {latestUser.lastname}
                        </p>
                        <p className="mt-1 truncate text-sm text-slate-400">
                          {latestUser.email}
                        </p>
                        <p className="mt-2 text-xs text-cyan-400">
                          {latestUser.role || "Utilisateur"}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="rounded-2xl border border-slate-800 bg-slate-950 p-5">
                <p className="text-slate-400">Aucune inscription récente.</p>
              </div>
            )}
          </div>

          <div className="rounded-2xl border border-violet-500/20 bg-slate-900 p-6 shadow-sm">
            <div className="mb-5 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-white">
                  Derniers abonnés newsletter
                </h2>
                <p className="mt-1 text-sm text-slate-400">
                  Les inscriptions newsletter les plus récentes.
                </p>
              </div>
              <button
                onClick={() => navigate("/dashboard/newsletter/subscribers")}
                className="rounded-xl border border-slate-700 px-4 py-2 text-sm font-medium text-slate-200 transition hover:bg-slate-800"
              >
                Voir les abonnés
              </button>
            </div>

            {latestNewsletterSubscribers.length > 0 ? (
              <div className="space-y-3">
                {latestNewsletterSubscribers.map((subscriber) => (
                  <div
                    key={subscriber.id}
                    className="rounded-xl border border-slate-800 bg-slate-950 p-4"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <p className="truncate font-semibold text-white">
                          {subscriber.email}
                        </p>
                        <p className="mt-2 text-xs text-slate-500">
                          {subscriber.subscribed_at
                            ? new Date(subscriber.subscribed_at).toLocaleDateString("fr-BE")
                            : "Date inconnue"}
                        </p>
                      </div>
                      <span
                        className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                          subscriber.is_active
                            ? "bg-green-500/15 text-green-300"
                            : "bg-red-500/15 text-red-300"
                        }`}
                      >
                        {subscriber.is_active ? "Actif" : "Désabonné"}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="rounded-2xl border border-slate-800 bg-slate-950 p-5">
                <p className="text-slate-400">Aucun abonné récent.</p>
              </div>
            )}
          </div>
        </section>

        <section className="mb-8 grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-5">
          {quickStats.map((item) => (
            <div
              key={item.label}
              className="rounded-2xl border border-slate-800 bg-slate-900 p-6 shadow-sm"
            >
              <p className="text-sm font-medium text-slate-400">{item.label}</p>
              <h2 className={`mt-2 text-3xl font-bold ${item.accent}`}>
                {item.value}
              </h2>
              <p className="mt-2 text-sm leading-6 text-slate-500">
                {item.helper}
              </p>
            </div>
          ))}
        </section>

        <section className="mb-8">
          <div className="mb-4">
            <h2 className="text-xl font-semibold text-white">Actions rapides</h2>
            <p className="mt-1 text-sm text-slate-400">
              Accédez directement aux principales actions du dashboard.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
            {quickActions.map((action) => (
              <button
                key={action.title}
                onClick={action.onClick}
                className={`rounded-2xl border border-slate-800 bg-slate-900 p-6 text-left shadow-sm transition ${action.className}`}
              >
                <div
                  className={`mb-4 flex h-12 w-12 items-center justify-center rounded-2xl text-2xl ${action.iconClassName}`}
                >
                  {action.emoji}
                </div>
                <h3 className="mb-2 text-lg font-semibold text-white">
                  {action.title}
                </h3>
                <p className="text-sm leading-6 text-slate-400">
                  {action.description}
                </p>
              </button>
            ))}
          </div>
        </section>

        <section className="grid grid-cols-1 gap-6 xl:grid-cols-3">
          <div className="xl:col-span-2 space-y-6">
            <div className="rounded-2xl border border-slate-800 bg-slate-900 p-6 shadow-sm">
              <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-white">
                    Derniers posts
                  </h2>
                  <p className="mt-1 text-sm text-slate-400">
                    Les 3 contenus les plus récents.
                  </p>
                </div>
                <button
                  onClick={() => navigate("/dashboard/posts")}
                  className="rounded-xl border border-slate-700 px-4 py-2 text-sm font-medium text-slate-200 transition hover:bg-slate-800"
                >
                  Voir tous les posts
                </button>
              </div>

              {latestPosts.length > 0 ? (
                <div className="space-y-4">
                  {latestPosts.map((post) => (
                    <div
                      key={post.id}
                      className="flex flex-col gap-4 rounded-2xl border border-slate-800 bg-slate-950 p-5 transition hover:border-slate-700 sm:flex-row sm:items-center sm:justify-between"
                    >
                      <div className="min-w-0">
                        <h3 className="truncate text-base font-semibold text-white">
                          {post.title}
                        </h3>
                        {post.description && (
                          <p className="mt-2 line-clamp-2 text-sm leading-6 text-slate-400">
                            {post.description}
                          </p>
                        )}
                        {post.categories && post.categories.length > 0 ? (
                          <div className="mt-3 flex flex-wrap gap-2">
                            {post.categories.map((category) => (
                              <span
                                key={category.id}
                                className="inline-flex rounded-full bg-blue-500/10 px-3 py-1 text-xs font-semibold text-blue-300"
                              >
                                {category.name}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <p className="mt-3 text-xs text-orange-400">
                            Aucune catégorie
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() => navigate(`/dashboard/posts/edit/${post.slug}`)}
                        className="shrink-0 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-500"
                      >
                        Modifier
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="rounded-2xl border border-slate-800 bg-slate-950 p-5">
                  <p className="text-slate-400">Aucun post récent.</p>
                </div>
              )}
            </div>

            <div className="rounded-2xl border border-slate-800 bg-slate-900 p-6 shadow-sm">
              <div className="mb-6 flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-white">
                    Derniers commentaires
                  </h2>
                  <p className="mt-1 text-sm text-slate-400">
                    Les dernières interactions reçues sur le site.
                  </p>
                </div>
              </div>

              {latestComments.length > 0 ? (
                <div className="space-y-4">
                  {latestComments.map((comment) => (
                    <div
                      key={comment.id}
                      className="rounded-2xl border border-slate-800 bg-slate-950 p-5"
                    >
                      <p className="text-sm font-semibold text-white">
                        {comment.user
                          ? `${comment.user.firstname || ""} ${comment.user.lastname || ""}`.trim()
                          : "Utilisateur inconnu"}
                      </p>
                      {comment.post && (
                        <p className="mt-1 text-xs text-blue-400">
                          Sur : {comment.post.title}
                        </p>
                      )}
                      <p className="mt-3 line-clamp-3 text-sm leading-6 text-slate-300">
                        {comment.content || comment.comment || "Commentaire sans contenu."}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="rounded-2xl border border-slate-800 bg-slate-950 p-5">
                  <p className="text-slate-400">Aucun commentaire récent.</p>
                </div>
              )}
            </div>
          </div>

          <aside className="space-y-6">
            <div className="rounded-2xl border border-slate-800 bg-slate-900 p-6 shadow-sm">
              <h2 className="mb-4 text-xl font-semibold text-white">
                Aperçu rapide
              </h2>
              <div className="space-y-4">
                <div className="rounded-2xl border border-slate-800 bg-slate-950 p-5">
                  <p className="mb-2 text-xs font-medium uppercase tracking-wide text-blue-400">
                    Contenu
                  </p>
                  <p className="text-sm leading-6 text-slate-300">
                    Vous avez actuellement{" "}
                    <span className="font-semibold text-white">{stats.posts}</span>{" "}
                    post{stats.posts > 1 ? "s" : ""} réparti
                    {stats.posts > 1 ? "s" : ""} dans{" "}
                    <span className="font-semibold text-white">{stats.categories}</span>{" "}
                    catégorie{stats.categories > 1 ? "s" : ""}.
                  </p>
                </div>
                <div className="rounded-2xl border border-slate-800 bg-slate-950 p-5">
                  <p className="mb-2 text-xs font-medium uppercase tracking-wide text-violet-400">
                    Newsletter
                  </p>
                  <p className="text-sm leading-6 text-slate-300">
                    <span className="font-semibold text-white">{stats.newsletter_active}</span>{" "}
                    abonné{stats.newsletter_active > 1 ? "s" : ""} actif
                    {stats.newsletter_active > 1 ? "s" : ""} et{" "}
                    <span className="font-semibold text-white">{stats.newsletter_inactive}</span>{" "}
                    désabonné{stats.newsletter_inactive > 1 ? "s" : ""}.
                  </p>
                </div>
                <div className="rounded-2xl border border-slate-800 bg-slate-950 p-5">
                  <p className="mb-2 text-xs font-medium uppercase tracking-wide text-amber-400">
                    Points d'attention
                  </p>
                  <div className="space-y-2 text-sm text-slate-300">
                    <p>
                      Posts sans catégorie :{" "}
                      <span className="font-semibold text-white">
                        {stats.posts_without_categories}
                      </span>
                    </p>
                    <p>
                      Posts sans photo :{" "}
                      <span className="font-semibold text-white">
                        {stats.posts_without_image}
                      </span>
                    </p>
                  </div>
                </div>
                <div className="rounded-2xl border border-slate-800 bg-slate-950 p-5">
                  <p className="mb-3 text-xs font-medium uppercase tracking-wide text-emerald-400">
                    Accès utiles
                  </p>
                  <div className="flex flex-col gap-3">
                    <button
                      onClick={() => navigate("/dashboard/users")}
                      className="rounded-xl border border-slate-700 px-4 py-3 text-left text-sm font-medium text-slate-200 transition hover:bg-slate-800"
                    >
                      Voir les utilisateurs
                    </button>
                    <button
                      onClick={() => navigate("/dashboard/newsletter")}
                      className="rounded-xl border border-slate-700 px-4 py-3 text-left text-sm font-medium text-slate-200 transition hover:bg-slate-800"
                    >
                      Ouvrir le module newsletter
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </aside>
        </section>
      </main>
    </div>
  );
};

export default Dashboard;